import { EyeOff, Table as TableIcon } from "@untitledui/icons";
import { type KeyboardEvent, type ReactNode, type RefObject, useEffect, useRef } from "react";
import type { Key, Selection } from "react-aria-components";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import type { ColumnCatalog } from "./column-layout";
import { ConfigureColumnsPopover } from "./configure-columns-popover";
import { Table, TableCard } from "./table";
import type {
  UnboxTableEmptyState,
  UnboxTableEmptyStateAction,
  UnboxTableProps,
  UnboxTableRow,
} from "./unbox-table.types";
import { useColumnLayout } from "./use-column-layout";
import { useScrollEdges } from "./use-scroll-edges";
import { SEEN_ROW_CLASS, useSeenRows } from "./use-seen-rows";

// App-wide default for "filter/search matched nothing" — only `onClearFilters` should need
// to vary per page.
const DEFAULT_EMPTY_STATE: UnboxTableEmptyState = { icon: TableIcon, title: "Nothing to see here yet" };

const DEFAULT_FILTERED_EMPTY_STATE: UnboxTableEmptyState = {
  icon: EyeOff,
  title: "Your search didn't return any results",
  description: "Try adjusting your filters or search terms to see more results.",
};

// Keeps the grid's arrow-key and typeahead handling away from text fields inside cells.
// Checkboxes are excluded so row selection still works from the keyboard.
const TEXT_FIELD_SELECTOR = "textarea, input:not([type=checkbox]):not([type=radio])";

function stopKeysFromTextFields(event: KeyboardEvent<HTMLElement>) {
  if (!event.currentTarget.contains(event.target as Node)) return;
  const target = event.target as HTMLElement;
  if (target.isContentEditable || target.matches(TEXT_FIELD_SELECTOR)) event.stopPropagation();
}

function EmptyStateActionButton({ action }: { action: UnboxTableEmptyStateAction }) {
  const Icon = action.icon;
  return (
    <Button size="md" color="tertiary" onClick={action.onClick}>
      <div className="flex items-center">
        {Icon && <Icon className="w-4 h-4 mr-2" />}
        {action.label}
      </div>
    </Button>
  );
}

export function UnboxTableEmptyStateContent({
  rows,
  hasActiveFilters,
  emptyState = DEFAULT_EMPTY_STATE,
  filteredEmptyState,
}: {
  rows: unknown[] | undefined;
  hasActiveFilters?: boolean;
  emptyState?: UnboxTableEmptyState;
  filteredEmptyState?: Partial<UnboxTableEmptyState> & { onClearFilters?: () => void };
}) {
  if (rows === undefined) {
    return (
      <EmptyState className="space-y-4" size="md">
        <LoadingIndicator />
        <EmptyState.Title>Loading...</EmptyState.Title>
      </EmptyState>
    );
  }

  if (hasActiveFilters && filteredEmptyState) {
    const icon = filteredEmptyState.icon ?? DEFAULT_FILTERED_EMPTY_STATE.icon;
    const title = filteredEmptyState.title ?? DEFAULT_FILTERED_EMPTY_STATE.title;
    const description = filteredEmptyState.description ?? DEFAULT_FILTERED_EMPTY_STATE.description;
    return (
      <EmptyState size="md">
        <div className="pb-6">
          <EmptyState.FeaturedIcon icon={icon} color="gray" className="bg-primary" />
        </div>
        <EmptyState.Content>
          <EmptyState.Title>{title}</EmptyState.Title>
          {description && <EmptyState.Description>{description}</EmptyState.Description>}
        </EmptyState.Content>
        {(filteredEmptyState.onClearFilters || filteredEmptyState.action) && (
          <EmptyState.Footer>
            {filteredEmptyState.action && <EmptyStateActionButton action={filteredEmptyState.action} />}
            {filteredEmptyState.onClearFilters && (
              <Button size="md" color="secondary" onClick={filteredEmptyState.onClearFilters}>
                Clear Filters
              </Button>
            )}
          </EmptyState.Footer>
        )}
      </EmptyState>
    );
  }

  return (
    <EmptyState size="md">
      <div className="pb-6">
        <EmptyState.FeaturedIcon icon={emptyState.icon ?? TableIcon} color="gray" className="bg-primary" />
      </div>
      <EmptyState.Content>
        <EmptyState.Title>{emptyState.title}</EmptyState.Title>
        {emptyState.description && <EmptyState.Description>{emptyState.description}</EmptyState.Description>}
      </EmptyState.Content>
      {emptyState.action && (
        <EmptyState.Footer>
          <EmptyStateActionButton action={emptyState.action} />
        </EmptyState.Footer>
      )}
    </EmptyState>
  );
}

function ConfigurableUnboxTable<Row extends UnboxTableRow>({
  columnCatalog,
  onHiddenSortColumn,
  ...props
}: UnboxTableProps<Row> & { columnCatalog: ColumnCatalog }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { layout, visibleColumns, labels, identityLabel, setLayout, resetLayout } = useColumnLayout(
    columnCatalog,
    props.columns,
  );
  const edges = useScrollEdges(containerRef, true);

  const sortColumn = props.sorting?.descriptor.column;
  const sortColumnHidden =
    sortColumn !== undefined &&
    columnCatalog.options.some((option) => option.id === sortColumn) &&
    !visibleColumns.some((column) => column.id === sortColumn);
  useEffect(() => {
    if (sortColumnHidden) onHiddenSortColumn?.();
  }, [sortColumnHidden, onHiddenSortColumn]);

  return (
    <UnboxTableView
      {...props}
      columns={visibleColumns}
      containerRef={containerRef}
      overlay={
        edges.headerHeight > 0 && (
          <>
            <div
              className="absolute top-0 right-0 z-20 flex w-[90px] items-center justify-end rounded-tr-xl bg-[linear-gradient(to_right,transparent,var(--color-unbox-cream-50)_50%)] pr-3"
              style={{ height: edges.headerHeight }}
            >
              <ConfigureColumnsPopover
                catalog={columnCatalog}
                layout={layout}
                identityLabel={identityLabel}
                labels={labels}
                onChange={setLayout}
                onReset={resetLayout}
              />
            </div>
            {(["left", "right"] as const).map((side) => (
              <div
                key={side}
                aria-hidden
                className={cx(
                  "pointer-events-none absolute bottom-0 z-10 w-11 from-[#E8D5C7] to-transparent transition-opacity duration-150 ease-out",
                  side === "left" ? "left-0 rounded-bl-xl bg-linear-to-r" : "right-0 rounded-br-xl bg-linear-to-l",
                  edges[side] ? "opacity-40" : "opacity-0",
                )}
                style={{ top: edges.headerHeight }}
              />
            ))}
          </>
        )
      }
    />
  );
}

export function UnboxTable<Row extends UnboxTableRow>(props: UnboxTableProps<Row>) {
  if (props.columnCatalog) return <ConfigurableUnboxTable {...props} columnCatalog={props.columnCatalog} />;
  return <UnboxTableView {...props} />;
}

function UnboxTableView<Row extends UnboxTableRow>({
  columns,
  rows,
  rowId,
  onClickRow,
  rowClassName,
  selection,
  sorting,
  pagination,
  hasActiveFilters,
  emptyState,
  filteredEmptyState,
  size = "sm",
  className,
  "aria-label": ariaLabel,
  containerRef,
  overlay,
}: UnboxTableProps<Row> & { containerRef?: RefObject<HTMLDivElement | null>; overlay?: ReactNode }) {
  const { isSeen, markSeen, seenCount } = useSeenRows(ariaLabel);
  const sortDescriptor = sorting
    ? { column: sorting.descriptor.column ?? "", direction: sorting.descriptor.direction }
    : undefined;

  const handleSelectionChange = (keys: Selection) => {
    if (!selection || selection.raw) return;
    const rowList = rows ?? [];
    const ids =
      keys === "all"
        ? new Set(rowList.map((row) => String(row[rowId])))
        : new Set(Array.from(keys).map((key) => String(key)));
    const resolvedRows = rowList.filter((row) => ids.has(String(row[rowId])));
    selection.onSelectionChange(ids, resolvedRows);
  };

  const selectedKeys = selection
    ? selection.raw
      ? selection.selectedKeys
      : new Set(selection.selectedRowIds as Set<Key>)
    : undefined;
  const onSelectionChangeHandler = selection
    ? selection.raw
      ? selection.onSelectionChange
      : handleSelectionChange
    : undefined;

  return (
    <div className="flex w-full flex-1 flex-col" onKeyDownCapture={stopKeysFromTextFields}>
      <div ref={containerRef} className="relative flex w-full flex-1 flex-col">
        {overlay}
        <TableCard.Root className={cx("flex w-full flex-col", className)} size={size} rounded>
          <Table
            aria-label={ariaLabel}
            selectionMode={selection ? "multiple" : "none"}
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChangeHandler}
            sortDescriptor={sortDescriptor}
            onSortChange={sorting?.onSortChange}
            className={!rows || rows.length === 0 ? "h-full" : undefined}
            onRowAction={
              onClickRow
                ? (key) => {
                    const row = (rows ?? []).find((r) => String(r[rowId]) === String(key));
                    if (!row) return;
                    markSeen(String(key));
                    onClickRow(row);
                  }
                : undefined
            }
          >
            <Table.Header
              className={cx(
                "sticky top-0 z-10 bg-unbox-cream-50 [&>th:first-child]:py-3 [&>th:first-child]:pr-4 [&>th:first-child]:pl-4",
                (!rows || rows.length === 0) && "hidden",
              )}
              allowSelectAll={!!selection}
            >
              {columns.map((column) => (
                <Table.Head
                  key={column.id}
                  id={column.id}
                  label={column.label}
                  isRowHeader={column === columns[0]}
                  allowsSorting={column.sortable}
                  tooltip={column.tooltip}
                  className="px-4 py-3"
                  style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
                />
              ))}
            </Table.Header>

            <Table.Body
              items={rows}
              dependencies={[columns, seenCount]}
              renderEmptyState={() => (
                <div className="flex h-[60vh] w-full items-center justify-center bg-unbox-cream-50">
                  <UnboxTableEmptyStateContent
                    rows={rows}
                    hasActiveFilters={hasActiveFilters}
                    emptyState={emptyState}
                    filteredEmptyState={filteredEmptyState}
                  />
                </div>
              )}
            >
              {(row: Row) => (
                <Table.Row id={String(row[rowId])} className={cx("bg-primary cursor-pointer", rowClassName?.(row))}>
                  {columns.map((column) => {
                    const value = column.rowAccessor ? row[column.rowAccessor] : undefined;
                    return (
                      <Table.Cell
                        key={column.id}
                        className={cx(
                          column.cellClassName,
                          column === columns[0] && isSeen(String(row[rowId])) && SEEN_ROW_CLASS,
                        )}
                        style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
                      >
                        {column.render ? column.render(value, row) : value == null ? "—" : String(value)}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      </div>

      {pagination && rows && rows.length > 0 && (
        <PaginationPageMinimalCenter
          page={pagination.page}
          total={pagination.totalPages}
          onPageChange={pagination.onPageChange}
          className="px-0 py-[20px] md:pb-2"
        />
      )}
    </div>
  );
}
